import { useState, useEffect } from "react";
import { Container, Typography, Box, Card, List, ListItem, ListItemText, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";

const SessionHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    // Load sessions from localStorage
    const sessionsData = JSON.parse(localStorage.getItem("sessions")) || [];
    setSessions(sessionsData);
  }, []);

  const deleteSession = (index) => {
    const updatedSessions = sessions.filter((_, i) => i !== index);
    setSessions(updatedSessions);
    localStorage.setItem("sessions", JSON.stringify(updatedSessions));
  };

  const handleOpenDialog = (session) => {
    setSelectedSession(session);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const exportSessions = (format) => {
    let dataStr = "";
    let filename = `session-history.${format}`;

    if (format === "csv") {
      dataStr = "Date,Type,Duration (minutes),Status\n";
      sessions.forEach((session) => {
        dataStr += `${session.completedAt},${session.type},${session.duration},completed\n`;
      });
    } else if (format === "json") {
      dataStr = JSON.stringify(sessions, null, 2);
      filename = "session-history.json";
    }

    const element = document.createElement("a");
    element.setAttribute("href", `data:text/plain;charset=utf-8,${encodeURIComponent(dataStr)}`);
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        align="center"
        sx={{
          fontWeight: 900,
          fontFamily: "'Montserrat', sans-serif",
          background: "linear-gradient(90deg, #FF6F61, #D84315)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textTransform: "uppercase",
          letterSpacing: "2px",
          mb: 4,
        }}
        gutterBottom
      >
        Session History
      </Typography>

      {/* Export Buttons */}
      <Box display="flex" gap={2} mb={4}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => exportSessions("csv")}
          sx={{ background: "linear-gradient(45deg, #4CAF50, #45a049)" }}
        >
          Export as CSV
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => exportSessions("json")}
          sx={{ background: "linear-gradient(45deg, #2196F3, #1976D2)" }}
        >
          Export as JSON
        </Button>
      </Box>

      {/* Sessions List */}
      {sessions.length > 0 ? (
        <List>
          {sessions.map((session, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <ListItem
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <ListItemText
                  primary={`${session.type} - ${new Date(session.completedAt).toLocaleString()}`}
                  secondary={
                    `Duration: ${session.duration} minutes` +
                    (session.taskText ? ` | Task: ${session.taskText}` : "")
                  }
                  onClick={() => handleOpenDialog(session)}
                  sx={{ cursor: "pointer" }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteSession(index)}
                >
                  Delete
                </Button>
              </ListItem>
            </Card>
          ))}
        </List>
      ) : (
        <Typography color="textSecondary" align="center">
          No session history yet. Start a pomodoro to log sessions!
        </Typography>
      )}

      {/* Session Details Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Session Details</DialogTitle>
        <DialogContent>
          {selectedSession && (
            <Box sx={{ pt: 2 }}>
              <Typography>
                <strong>Type:</strong> {selectedSession.type}
              </Typography>
              <Typography>
                <strong>Date & Time:</strong> {new Date(selectedSession.completedAt).toLocaleString()}
              </Typography>
              <Typography>
                <strong>Duration:</strong> {selectedSession.duration} minutes
              </Typography>
              {selectedSession.taskText && (
                <Typography>
                  <strong>Task:</strong> {selectedSession.taskText}
                </Typography>
              )}
              <Typography>
                <strong>Status:</strong> Completed
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SessionHistory;
